/**
 * Channel Jewelry — Premium Ring Styles Mega Menu
 *
 * Data-driven from assets/mega-menu-data.json (fetched once and cached) for the
 * style list, images and copy. The SHAPE COUNTS are LIVE: after the JSON loads,
 * the script reads each collection's tag feed (templates/collection.cj-counts.liquid,
 * a tiny text feed) and counts "style tag + shape tag" products itself, so the
 * numbers always match the real collection pages. Results are cached in
 * sessionStorage for 10 minutes. If the live feed fails or is slow, the static
 * "shapes" counts in mega-menu-data.json are used as a fallback.
 * Kill switch: set window.cjMegaMenuLive = false before this script to use only
 * the static counts.
 *
 * ALSO (added 2026-10-24): on the /pages/collections page (sections/cj-collections.liquid)
 * this script replaces the typed-in product counts (nav pills, category / ring-style /
 * diamond-shape cards, "Featured N / View all N" labels and the Products stat) with the
 * real collection sizes from the same feed. Kill switch: window.cjCollectionsLive = false.
 *
 * Renders the style tabs, shape grid, and featured panel; swaps
 * the shape grid + featured panel content on hover/focus of a style tab
 * with no clicks required. Desktop only -- mobile uses native <details>
 * accordion markup rendered server-side in the Liquid snippet, so no JS
 * is needed for the mobile experience at all.
 *
 * Interaction contract (per spec):
 *  - hover a style tab -> shape grid + featured panel update instantly
 *  - menu stays open while moving between tabs / shape grid / featured panel
 *  - ~200ms grace period before closing when the pointer leaves entirely
 *  - keyboard: focusing a tab (Tab key) does the same as hovering it;
 *    Escape closes the menu and returns focus to the "Collections" link
 */
(function () {
  "use strict";

  const CLOSE_DELAY_MS = 200;
  const DATA_URL = window.cjMegaMenuDataUrl;

  // ---- live count settings ------------------------------------------------
  const LIVE_TTL_MS = 10 * 60 * 1000; // cache live counts for 10 minutes
  const LIVE_WAIT_MS = 1500; // max wait before rendering with static numbers
  const LIVE_CACHE_KEY = "cjMegaLiveCounts.v1";
  const SHAPE_TAGS = [
    "round", "princess", "oval", "cushion", "emerald", "pear",
    "marquise", "asscher", "heart", "radiant", "mixed", "none",
  ];

  let menuData = null;
  let dataPromise = null;
  const loadedImages = new Set();

  // ---- live counts ----------------------------------------------------------
  function normTag(s) {
    return String(s).toLowerCase().replace(/[\s_-]+/g, " ").trim();
  }

  function titleCase(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function readLiveCache() {
    try {
      const c = JSON.parse(sessionStorage.getItem(LIVE_CACHE_KEY));
      if (c && c.shapes && Date.now() - c.t < LIVE_TTL_MS) return c.shapes;
    } catch (e) {}
    return null;
  }

  function writeLiveCache(shapes) {
    try {
      sessionStorage.setItem(LIVE_CACHE_KEY, JSON.stringify({ t: Date.now(), shapes: shapes }));
    } catch (e) {}
  }

  // Returns an array of Sets (one per product) holding that product's tags.
  async function fetchCollectionTags(handle) {
    const base = `/collections/${handle}?view=cj-counts`;
    const firstRes = await fetch(base, { credentials: "omit" });
    if (!firstRes.ok) throw new Error(`feed ${handle} ${firstRes.status}`);
    const first = await firstRes.text();
    const m = first.match(/^\s*CJ\|(\d+)/);
    if (!m) throw new Error(`bad feed for ${handle}`);
    const pages = Math.min(parseInt(m[1], 10) || 1, 40);
    const texts = [first];
    const rest = [];
    for (let p = 2; p <= pages; p++) {
      rest.push(
        fetch(`${base}&page=${p}`, { credentials: "omit" }).then((r) => {
          if (!r.ok) throw new Error(`feed ${handle} p${p} ${r.status}`);
          return r.text();
        })
      );
    }
    texts.push(...(await Promise.all(rest)));
    const products = [];
    texts.forEach((t) =>
      t.split("\n").forEach((line) => {
        line = line.trim();
        if (!line || /^CJ\|/.test(line)) return;
        products.push(new Set(line.split("|").map(normTag)));
      })
    );
    return products;
  }

  async function fetchLiveShapes(data) {
    const handles = [...new Set(Object.values(data.styles).map((s) => s.collection))];
    const byCollection = {};
    await Promise.all(
      handles.map(async (h) => {
        byCollection[h] = await fetchCollectionTags(h);
      })
    );
    const out = {};
    Object.entries(data.styles).forEach(([styleName, style]) => {
      const styleTag = normTag(style.tag);
      const counts = {};
      (byCollection[style.collection] || []).forEach((tags) => {
        if (!tags.has(styleTag)) return;
        SHAPE_TAGS.forEach((shape) => {
          if (tags.has(shape)) {
            const label = titleCase(shape);
            counts[label] = (counts[label] || 0) + 1;
          }
        });
      });
      out[styleName] = counts;
    });
    return out;
  }

  // Overwrites style.shapes with live counts. Styles with no live matches keep
  // their static fallback. Returns true if anything was applied.
  function applyLiveShapes(data, shapes) {
    let applied = false;
    if (!shapes) return applied;
    Object.entries(shapes).forEach(([styleName, counts]) => {
      if (data.styles[styleName] && Object.keys(counts).length) {
        data.styles[styleName].shapes = counts;
        applied = true;
      }
    });
    return applied;
  }

  function attachLiveCounts(data) {
    if (window.cjMegaMenuLive === false) return Promise.resolve(data);

    const cached = readLiveCache();
    if (cached) {
      applyLiveShapes(data, cached);
      return Promise.resolve(data);
    }

    const livePromise = fetchLiveShapes(data)
      .then((shapes) => {
        writeLiveCache(shapes);
        return shapes;
      })
      .catch((err) => {
        console.warn("[cj-mega-menu] live counts unavailable, using static numbers", err);
        return null;
      });

    const timeout = new Promise((resolve) => setTimeout(() => resolve("timeout"), LIVE_WAIT_MS));

    return Promise.race([livePromise, timeout]).then((result) => {
      if (result === "timeout") {
        // Render now with static numbers, then refresh when live data arrives.
        livePromise.then((shapes) => {
          if (applyLiveShapes(data, shapes)) {
            document.dispatchEvent(new CustomEvent("cj-mega-live-updated"));
          }
        });
      } else {
        applyLiveShapes(data, result);
      }
      return data;
    });
  }

  function fetchData() {
    if (dataPromise) return dataPromise;
    dataPromise = fetch(DATA_URL, { credentials: "omit" })
      .then((r) => r.json())
      .then((json) => {
        menuData = json;
        return attachLiveCounts(json);
      })
      .catch((err) => {
        console.error("[cj-mega-menu] failed to load mega-menu-data.json", err);
        return null;
      });
    return dataPromise;
  }

  // ---- /pages/collections live product counts --------------------------------
  // The page (sections/cj-collections.liquid) has hand-typed product counts. This
  // swaps them for each collection's real size, read from the first line of the
  // feed ("CJ|<pages>|<total products>"). Typed numbers stay as the fallback.
  const COLLECTIONS_CACHE_KEY = "cjCollectionCounts.v1";
  const CATEGORY_HANDLES = [
    "engagement-rings", "wedding-bands", "fashion-rings",
    "earrings", "necklace", "bracelets",
  ];

  function fetchCollectionTotal(handle) {
    return fetch(`/collections/${handle}?view=cj-counts`, { credentials: "omit" })
      .then((r) => (r.ok ? r.text() : ""))
      .then((t) => {
        const m = t.match(/^\s*CJ\|\d+\|(\d+)/);
        return m ? parseInt(m[1], 10) : null;
      })
      .catch(() => null);
  }

  function applyCollectionsPageCounts(c) {
    // Jump-nav pills (Earrings / Necklaces / Bracelets)
    const pillMap = { "#earrings": "earrings", "#necklaces": "necklace", "#bracelets": "bracelets" };
    document.querySelectorAll(".cj-cat-nav-link").forEach((a) => {
      const h = pillMap[a.getAttribute("href")];
      const el = a.querySelector(".cj-cat-nav-count");
      if (h && el && typeof c[h] === "number") el.textContent = c[h];
    });

    // Category / ring style / diamond shape cards
    document.querySelectorAll(".cj-style-card").forEach((card) => {
      const m = (card.getAttribute("href") || "").match(/^\/collections\/([^/?#]+)$/);
      if (!m) return;
      const n = c[m[1]];
      const el = card.querySelector(".cj-style-card-count");
      if (el && typeof n === "number") el.textContent = n + " Products";
    });

    // Earrings / Necklaces / Bracelets section headers: "Featured N · View all M →"
    [["earrings", "earrings"], ["necklaces", "necklace"], ["bracelets", "bracelets"]].forEach(
      ([sectionId, handle]) => {
        const section = document.getElementById(sectionId);
        const n = c[handle];
        if (!section || typeof n !== "number") return;
        const el = section.querySelector(".cj-cat-section-count");
        if (!el) return;
        const featured = section.querySelectorAll(".cj-prod-card").length;
        el.innerHTML =
          `Featured ${featured} &middot; <a href="/collections/${handle}" ` +
          `style="color:var(--teal);text-decoration:underline;text-underline-offset:3px">` +
          `View all ${n} &rarr;</a>`;
      }
    );

    // "Products" stat = sum of the six category collections
    let sum = 0;
    let complete = true;
    CATEGORY_HANDLES.forEach((h) => {
      if (typeof c[h] === "number") sum += c[h];
      else complete = false;
    });
    if (complete) {
      document.querySelectorAll(".cj-stat").forEach((stat) => {
        const label = stat.querySelector(".cj-stat-label");
        const num = stat.querySelector(".cj-stat-number");
        if (!label || !num || !/^products$/i.test(label.textContent.trim())) return;
        num.setAttribute("data-count-to", sum);
        if (num.textContent !== "0" && /^[0-9,]+\+?$/.test(num.textContent)) {
          num.textContent = sum.toLocaleString() + (num.dataset.suffix || "+");
        }
      });
    }
  }

  function initCollectionsPageCounts() {
    if (window.cjCollectionsLive === false) return;
    if (!document.querySelector(".cj-cat-nav")) return; // only the Collections page has this

    const handles = new Set(CATEGORY_HANDLES);
    document.querySelectorAll(".cj-style-card").forEach((card) => {
      const m = (card.getAttribute("href") || "").match(/^\/collections\/([^/?#]+)$/);
      if (m) handles.add(m[1]);
    });

    try {
      const raw = JSON.parse(sessionStorage.getItem(COLLECTIONS_CACHE_KEY));
      if (raw && raw.c && Date.now() - raw.t < LIVE_TTL_MS) {
        applyCollectionsPageCounts(raw.c);
        return;
      }
    } catch (e) {}

    const out = {};
    Promise.all(
      [...handles].map((h) =>
        fetchCollectionTotal(h).then((n) => {
          if (typeof n === "number") out[h] = n;
        })
      )
    ).then(() => {
      if (!Object.keys(out).length) return;
      applyCollectionsPageCounts(out);
      try {
        sessionStorage.setItem(COLLECTIONS_CACHE_KEY, JSON.stringify({ t: Date.now(), c: out }));
      } catch (e) {}
    });
  }

  function buildShapeUrl(style, shapeName) {
    const styleTag = style.tag.replace(/\s+/g, "-").toLowerCase();
    const shapeTag = shapeName.replace(/\s+/g, "-").toLowerCase();
    return `/collections/${style.collection}/${styleTag}+${shapeTag}`;
  }

  function buildStyleUrl(style) {
    const styleTag = style.tag.replace(/\s+/g, "-").toLowerCase();
    return `/collections/${style.collection}/${styleTag}`;
  }

  function renderShapeGrid(container, style) {
    const entries = Object.entries(style.shapes).sort((a, b) => b[1] - a[1]);
    container.innerHTML = entries
      .map(
        ([shapeName, count]) => `
        <a class="cj-mega-shape-link" href="${buildShapeUrl(style, shapeName)}">
          <span class="cj-mega-shape-name">${shapeName}<span class="cj-mega-shape-count">(${count})</span></span>
          <span class="cj-mega-shape-arrow" aria-hidden="true">&rarr;</span>
        </a>`
      )
      .join("");
    container.classList.remove("cj-mega-panel-fade");
    void container.offsetWidth; // restart animation
    container.classList.add("cj-mega-panel-fade");
  }

  function renderFeatured(panel, style) {
    const img = panel.querySelector(".cj-mega-featured-img-wrap img");
    const badgeEl = panel.querySelector(".cj-mega-badge");
    const titleEl = panel.querySelector(".cj-mega-featured-title");
    const descEl = panel.querySelector(".cj-mega-featured-desc");
    const ctaEl = panel.querySelector(".cj-mega-featured-cta");
    const viewAllEl = panel.querySelector(".cj-mega-featured-viewall");

    if (!loadedImages.has(style.image)) {
      img.classList.remove("cj-loaded");
    }
    img.src = style.image;
    img.alt = style.title;
    img.onload = () => {
      img.classList.add("cj-loaded");
      loadedImages.add(style.image);
    };

    if (style.badge) {
      badgeEl.textContent = style.badge;
      badgeEl.hidden = false;
    } else {
      badgeEl.hidden = true;
    }

    titleEl.textContent = style.title;
    descEl.textContent = style.description;
    ctaEl.textContent = `${style.button} →`;
    ctaEl.href = buildStyleUrl(style);
    viewAllEl.href = buildStyleUrl(style);

    panel.classList.remove("cj-mega-panel-fade");
    void panel.offsetWidth;
    panel.classList.add("cj-mega-panel-fade");
  }

  function setActiveTab(root, styleName) {
    const data = menuData;
    if (!data || !data.styles[styleName]) return;
    const style = data.styles[styleName];

    root.querySelectorAll(".cj-mega-tab").forEach((tab) => {
      const isActive = tab.dataset.style === styleName;
      tab.classList.toggle("cj-mega-tab--active", isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    renderShapeGrid(root.querySelector(".cj-mega-shape-grid"), style);
    renderFeatured(root.querySelector(".cj-mega-featured"), style);
  }

  function renderTabs(container, data) {
    container.innerHTML = data.order
      .map(
        (styleName, i) => `
        <button type="button" class="cj-mega-tab" data-style="${styleName}"
          role="tab" aria-selected="${i === 0 ? "true" : "false"}">
          ${styleName}
        </button>`
      )
      .join("");
  }

  function initDesktopMenu(root, triggerLi, data) {
    let closeTimer = null;

    function openMenu() {
      clearTimeout(closeTimer);
      root.classList.add("cj-mega--open");
      triggerLi.classList.add("cj-nav-item--mega-open");
    }
    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        root.classList.remove("cj-mega--open");
        triggerLi.classList.remove("cj-nav-item--mega-open");
      }, CLOSE_DELAY_MS);
    }
    function cancelClose() {
      clearTimeout(closeTimer);
    }

    renderTabs(root.querySelector(".cj-mega-tabs"), data);
    setActiveTab(root, data.order[0]);

    triggerLi.addEventListener("mouseenter", openMenu);
    triggerLi.addEventListener("mouseleave", scheduleClose);
    root.addEventListener("mouseenter", cancelClose);
    root.addEventListener("mouseleave", scheduleClose);

    root.querySelectorAll(".cj-mega-tab").forEach((tab) => {
      tab.addEventListener("mouseenter", () => setActiveTab(root, tab.dataset.style));
      tab.addEventListener("focus", () => {
        openMenu();
        setActiveTab(root, tab.dataset.style);
      });
    });

    // Keyboard: Escape closes and returns focus to the trigger link
    root.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        root.classList.remove("cj-mega--open");
        triggerLi.classList.remove("cj-nav-item--mega-open");
        triggerLi.querySelector("a").focus();
      }
    });

    // Closing focus outside the whole component
    triggerLi.addEventListener("focusout", (e) => {
      if (!triggerLi.contains(e.relatedTarget)) {
        scheduleClose();
      }
    });
  }

  function renderMobileAccordion(container, data) {
    container.innerHTML = data.order
      .map((styleName) => {
        const style = data.styles[styleName];
        const shapeLinks = Object.entries(style.shapes)
          .sort((a, b) => b[1] - a[1])
          .map(
            ([shapeName, count]) => `
            <a class="cj-mega-shape-link" href="${buildShapeUrl(style, shapeName)}">
              <span class="cj-mega-shape-name">${shapeName}<span class="cj-mega-shape-count">(${count})</span></span>
            </a>`
          )
          .join("");
        return `
        <details class="cj-mega-accordion-style">
          <summary class="cj-mega-accordion-style-btn">
            <span>${styleName}</span>
            <span class="cj-mega-accordion-icon" aria-hidden="true">&#8964;</span>
          </summary>
          <div class="cj-mega-accordion-panel">
            <img loading="lazy" src="${style.image}" alt="${style.title}">
            <div class="cj-mega-featured-title">${style.title}</div>
            <p class="cj-mega-featured-desc">${style.description}</p>
            <a class="cj-mega-featured-cta" href="${buildStyleUrl(style)}">${style.button} &rarr;</a>
            <div class="cj-mega-accordion-shapes">${shapeLinks}</div>
          </div>
        </details>`;
      })
      .join("");
  }

  function init() {
    const triggerLi = document.querySelector(".cj-nav-item-has-dropdown");
    const root = document.querySelector(".cj-mega");
    const mobileContainer = document.querySelector(".cj-mega-accordion");

    initCollectionsPageCounts();

    fetchData().then((data) => {
      if (!data) return;

      if (triggerLi && root) {
        initDesktopMenu(root, triggerLi, data);
        // QA aid, harmless in production: ?debug_style=Split%20Shank forces
        // that tab active and the panel open on load, no hover needed --
        // useful since this store's headless-screenshot tooling has been
        // unreliable for capturing real hover states.
        const debugStyle = new URLSearchParams(location.search).get("debug_style");
        if (debugStyle && data.styles[debugStyle]) {
          root.classList.add("cj-mega--open");
          setActiveTab(root, debugStyle);
        }
      }
      if (mobileContainer) {
        renderMobileAccordion(mobileContainer, data);
      }

      // If live counts arrive after the first render, refresh the numbers.
      document.addEventListener("cj-mega-live-updated", () => {
        if (triggerLi && root) {
          const active = root.querySelector(".cj-mega-tab--active");
          setActiveTab(root, active ? active.dataset.style : data.order[0]);
        }
        if (mobileContainer) {
          renderMobileAccordion(mobileContainer, data);
        }
      });

      // Preload the first style's featured image so the first open has no layout shift.
      const preload = new Image();
      preload.src = data.styles[data.order[0]].image;
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
